import { lazy, Suspense, useEffect } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'

import { Cog8ToothIcon } from '@heroicons/react/24/outline'

import useServer from '@/api/useServerMain'
import { Loading } from '@/components'
import Breadcrumb, { PathInterface } from '@/components/navigation/Breadcrumb'
import AppContainer from '@/containers/AppContainer'
import AreaList from '@/containers/areas/AreaList'
import CategoryList from '@/containers/logistic/categories/CategoryList'
import CompanyList from '@/containers/logistic/companies/CompanyList'
import ContainerList from '@/containers/logistic/containers/ContainerList'
import PurchaseOrderList from '@/containers/logistic/purchaseOrders/PurchaseOrderList'
import ShipmentNotificationList from '@/containers/logistic/shipmentNotifications/ShipmentNotificationList'
import VehicleList from '@/containers/logistic/vehicles/VehicleList'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessPackagesQuery } from '@/hooks/business/useBusinessPackagesQuery'
import { HomePage } from '@/pages'
import NotFoundPage from '@/pages/NotFoundPage'
import { useAppSelector } from '@/store/hooks'

//Areas
const ListAreas = lazy(() => import('@/containers/areas/ListAreas'))

const DetailAreaContainer = lazy(
  () => import('@/containers/areas/DetailAreaContainer')
)

//Shared Areas
const ListShareAreas = lazy(
  () => import('@/containers/config/GeneralAdjustment/shareArea/ListShareArea')
)

//Payment Gateways
const PaymentGatewaysList = lazy(
  () =>
    import(
      '@/containers/config/GeneralAdjustment/paymentGateways/PaymentGatewaysList'
    )
)

//Modules and Services
const ListModulesServices = lazy(
  () => import('@/containers/config/GeneralAdjustment/Visual')
)

//Almacenes
const ListStocks = lazy(() => import('../containers/areas/ListStocks'))

// Cupones
const ListCoupons = lazy(
  () => import('../containers/products/coupons/ListCoupons')
)

// Ciclos económicos
const OrdersEcoCycle = lazy(
  () => import('../containers/economicCycles/OrdersEcoCycle')
)

// Categorías de clientes
const ListCustomerCategory = lazy(
  () => import('../containers/onlineClients/categories/CategoriesRegisterList')
)

// Registro de asistencia
const AttendanceRecord = lazy(
  () => import('../containers/humanResources/attendanceRecord/AttendanceRecord')
)

// Generador de salarios
const Generator = lazy(
  () => import('../containers/humanResources/salary/all/Generator')
)

// Reglas de salarios
const ListRules = lazy(
  () => import('../containers/humanResources/salary/rules/ListRules')
)

// Historial de salarios
const Historical = lazy(
  () => import('../containers/humanResources/salary/historical/Historical')
)
const HistoricalDetails = lazy(
  () =>
    import('../containers/humanResources/salary/historical/HistoricalDetails')
)

const SalaryAnalysis = lazy(
  () => import('../containers/humanResources/salary/analysis/SalaryAnalysis')
)

// Resumen de facturación
const BillingSummary = lazy(
  () => import('../containers/billing/summary/BillingSummary')
)

// Reservas
const CalendarReservation = lazy(
  () => import('../containers/reservations//calendar/CalendarReservation')
)
const AllReservations = lazy(
  () =>
    import(
      '../containers/reservations/calendar/allReservations/AllReservations'
    )
)
const ReservationSettingsMain = lazy(
  () => import('../containers/reservations/settings/ReservationSettingsMain')
)

// Reportes
const AllReports = lazy(() => import('../containers/analysis/AllReports'))

// Órdenes de tienda
const ListAllOrders = lazy(() => import('../containers/store/ListAllOrders'))

// Cartelera digital
const CartDigital = lazy(() => import('../containers/cardDigital/CartDigital'))

// Configuraciones
const RegionConfig = lazy(() => import('../containers/config/RegionConfig'))
const ConfigMain = lazy(() => import('../containers/config/ConfigMain'))
const PriceSystemConfig = lazy(
  () => import('../containers/config/PriceSystemConfig')
)
const FixedCosts = lazy(() => import('../containers/config/FixedCosts'))
const CurrenciesConfig = lazy(
  () => import('../containers/config/CurrenciesConfig')
)

const DetailStockContainer = lazy(
  () => import('@/containers/areas/stock/DetailStockContainer')
)

const ListStockDispatches = lazy(
  () => import('@/containers/areas/stock/dispatch/ListStockDispatches')
)
const ListStockMovements = lazy(
  () => import('@/containers/areas/stock/listMovements/ListStockMovements')
)
const ListStockDetailsMovements = lazy(
  () => import('@/containers/areas/stock/listMovements/StockMovements')
)

const ReportContainer = lazy(
  () => import('@/containers/areas/stock/reports/ReportContainer')
)

const BuyReceiptList = lazy(
  () => import('@/containers/areas/stock/buy/BuyReceiptList')
)
const CargoAnalisis = lazy(
  () => import('@/containers/areas/stock/buy/CargoAnalisis')
)

const ListOfOriginDestiny = lazy(
  () => import('@/containers/areas/stock/ListOfOriginDestiny')
)

//Productos
const ListProducts = lazy(() => import('@/containers/products/ListProducts'))

const ListAllProductsReadyForSale = lazy(
  () => import('@/containers/products/ListAllProductsReadyForSale')
)

const ListStockCategories = lazy(
  () => import('@/containers/products/ListStockCategories')
)
const ListResource = lazy(() => import('@/containers/products/ListResource'))

const ListSalesCategories = lazy(
  () => import('@/containers/products/ListSalesCategories')
)

//Órdenes de producción
const ListProductionOrders = lazy(
  () => import('@/containers/productionOrders/orders/ListProductionOrders')
)

//Recetario
const ListRecipes = lazy(
  () => import('@/containers/productionOrders/recipe/ListRecipes')
)

//Analisis
const ProductionAnalysis = lazy(
  () =>
    import('@/containers/productionOrders/analysis/ProductionOrdersAnalysis')
)

//Ciclos económicos
const ListEcoCyclesPage = lazy(
  () => import('@/containers/economicCycles/ListEcoCycles')
)

const DetailEcoCycleContainer = lazy(
  () => import('@/containers/economicCycles/DetailEcoCycleContainer')
)

const EconomicCyclesAnalysis = lazy(
  () => import('@/containers/economicCycles/analysis/EconomicCyclesAnalysis')
)

//Cuentas bancarias
const ListBankAccounts = lazy(
  () => import('@/containers/bankAccount/ListBankAccounts')
)

const MainBankAccount = lazy(
  () => import('@/containers/bankAccount/bankAccounts/MainBankAccount')
)

const MainBankAccountReport = lazy(
  () => import('@/containers/bankAccount/reports/MainBankAccountReport')
)

const ListBalance = lazy(
  () => import('@/containers/bankAccount/listbalance/ListBalance')
)

const BankAccountAnalysis = lazy(
  () => import('@/containers/bankAccount/analysis/BankAccountAnalysis')
)

const BankAccountFinancialState = lazy(
  () => import('@/containers/bankAccount/reports/FinancialState')
)

//Usuarios
const ListUsers = lazy(
  () => import('@/containers/humanResources/users/ListUsers')
)
const ListPeople = lazy(
  () => import('@/containers/humanResources/persons/ListPeople')
)
const ListCategories = lazy(
  () => import('@/containers/humanResources/categories/ListCategories')
)
const ListPost = lazy(
  () => import('@/containers/humanResources/post/ListPosts')
)
// const ListAccessRecord = lazy(() => import("../containers/humanResources/ListAccessRecord"));
const SalaryContainer = lazy(
  () => import('@/containers/humanResources/salary/SalaryContainer')
)

//Clientes
const OnlineClientsContainer = lazy(
  () => import('@/containers/onlineClients/ClientRegisterList')
)

const DetailOnlineClientContainer = lazy(
  () => import('@/containers/onlineClients/ClientRegister')
)

//Proveedores
const ListSuppliers = lazy(() => import('@/containers/suppliers/ListSuppliers'))

const DetailSupplier = lazy(
  () => import('@/containers/suppliers/DetailSupplier')
)

//Facturación
const BillingRegisterList = lazy(
  () => import('@/containers/billing/register/BillingRegisterList')
)
const BillingRegister = lazy(
  () => import('@/containers/billing/register/BillingRegister')
)

const AllRegistersList = lazy(
  () => import('@/containers/billing/register/AllRegistersList')
)

// Operaciones de caja
const CashOperationsList = lazy(
  () => import('@/containers/cashOperations/CashOperationsList')
)

// Cobros vencidos
const OverduePayments = lazy(
  () => import('@/containers/billing/overduePayments/OverduePayments')
)

// Pagos anticipados
const AllPrepaidList = lazy(
  () => import('@/containers/billing/prepaid/AllPrepaidList')
)

// Análisis
const BillingAnalysis = lazy(
  () => import('@/containers/billing/analysis/BillingAnalysis')
)

// Modulo de notificaciones
const NotificationConfig = lazy(
  () => import('@/containers/config/notifications/NotificationsConfig')
)
const CargoScreen = lazy(
  () => import('@/containers/logistic/cargo/CargoScreen')
)

const ExpedientList = lazy(
  () => import('@/containers/logistic/expedients/ExpedientList')
)

//Tienda
const StoreGalery = lazy(() => import('@/containers/store/StoreGalery'))

//Configurations
const MyBusiness = lazy(() => import('@/containers/config/MyBusiness'))

//Turns
const TurnPage = lazy(() => import('../containers/turns/all/TurnPage'))

const PayedRoute = () => {
  const { business } = useAppSelector((state) => state.init)
  const { allowRoles, allowModules } = useServer()
  const navigate = useNavigate()
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  const { businessPackages } = useBusinessPackagesQuery({
    params: { businessId: businessData?.id ?? 0 },
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  useEffect(() => {
    if (!allowRoles(['ADMIN'])) {
      if (allowRoles(['MANAGER_AREA'], true)) {
        navigate('/stocks')
      } else if (allowRoles(['MANAGER_CONTABILITY'], true)) {
        navigate('/bank_accounts')
      } else if (allowRoles(['CHIEF_PRODUCTION'], true)) {
        navigate('/orders')
      } else if (
        allowRoles(['MANAGER_SHIPPING', 'MANAGER_SHOP_ONLINE'], true)
      ) {
        navigate('/online_shop')
      } else if (
        allowRoles(
          [
            'MANAGER_ECONOMIC_CYCLE',
            'MANAGER_SHIFT',
            'MANAGER_SALES',
            'ADMIN',
            'MANAGER_SHOP_ONLINE',
          ],
          true
        )
      ) {
        navigate('/ecocycle')
      } else if (allowRoles(['ANALYSIS_REPORT'], true)) {
        navigate('/')
      } else if (
        allowRoles(['MANAGER_COST_PRICES', 'PRODUCT_PROCESATOR'], true)
      ) {
        navigate('/products/all')
      } else if (allowRoles(['MANAGER_CUSTOMERS'], true)) {
        navigate('/clients/all')
      } else if (allowRoles(['MANAGER_SUPPLIERS'], true)) {
        navigate('/suppliers')
      } else if (allowRoles(['MANAGER_HUMAN_RESOURCES'], true)) {
        // !!business?.configurationsKey.find(
        //   (itm) => itm.key === 'module_human_resources' && itm.value === 'true'
        // )
        !!businessPackages?.find(
          (itm) => itm.code === 'M_HUMAN_RESOURCES' && itm.isActive
        )
          ? navigate('/human_resources/people')
          : navigate('/human_resources/users')
      } else if (allowRoles(['MANAGER_SALARY_RULES'], true)) {
        navigate('/salary/generator')
      } else if (allowRoles(['MANAGER_CURRENCIES'], true)) {
        navigate('/configurations/currencies')
      } else if (allowRoles(['MANAGER_CONFIGURATIONS'], true)) {
        navigate('/configurations/my_business')
      } else if (allowRoles(['MANAGER_BILLING'], true)) {
        navigate('/billing/registers')
      } else if (allowRoles(['MARKETING_SALES'], true)) {
        navigate('/products/sales')
      } else if (allowRoles(['BUYER'], true)) {
        navigate('/load/receipt')
      }
    }
  }, [])

  const module_booking =
    business?.configurationsKey.find((itm) => itm.key === 'module_booking')
      ?.value === 'true'

  return (
    <Routes>
      <Route path="/" element={<AppContainer />}>
        {/**Home */}
        {allowRoles(['ADMIN', 'ANALYSIS_REPORT']) && (
          <Route index element={<HomePage />} />
        )}
        {/**Stocks */}
        {allowRoles(['ADMIN', 'MANAGER_AREA', 'ANALYSIS_REPORT', 'BUYER']) && (
          <>
            <Route
              index={allowRoles(['MANAGER_AREA'], true)}
              path="/stocks"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStocks />
                </Suspense>
              }
            />
            <Route
              path="/stocks/:stockId"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailStockContainer />
                </Suspense>
              }
            />
            <Route
              path="/stocks/movements"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockMovements />
                </Suspense>
              }
            />
            <Route
              path="/stocks/movements/:stockId"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockDetailsMovements />
                </Suspense>
              }
            />
            <Route
              path="/stocks/dispatches"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListStockDispatches />
                </Suspense>
              }
            />
            <Route
              path="/stocks/analysis"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ReportContainer />
                </Suspense>
              }
            />
            <Route
              path="/load/receipt"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BuyReceiptList />
                </Suspense>
              }
            />
            <Route
              path="/load/origin_destiny"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListOfOriginDestiny />
                </Suspense>
              }
            />
            <Route
              path="/load/analisis"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CargoAnalisis />
                </Suspense>
              }
            />
          </>
        )}
        {/**Products */}
        {allowRoles([
          'ADMIN',
          'PRODUCT_PROCESATOR',
          'MANAGER_COST_PRICES',
          'MANAGER_BILLING',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
          'MARKETING_SALES',
          'MANAGER_AREA',
        ]) && (
          <>
            {allowRoles([
              'ADMIN',
              'PRODUCT_PROCESATOR',
              'MANAGER_COST_PRICES',
              'MANAGER_AREA',
            ]) && (
              <>
                <Route
                  index={allowRoles(
                    [
                      'PRODUCT_PROCESATOR',
                      'MANAGER_COST_PRICES',
                      'MANAGER_AREA',
                    ],
                    true
                  )}
                  path="/products/all"
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <ListProducts />
                    </Suspense>
                  }
                />
                {allowRoles([
                  'ADMIN',
                  'PRODUCT_PROCESATOR',
                  'MANAGER_COST_PRICES',
                ]) && (
                  <>
                    <Route
                      path="/products/sales_categories"
                      element={
                        <Suspense fallback={<Loading loading={false} />}>
                          <ListSalesCategories />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/products/general_categories"
                      element={
                        <Suspense fallback={<Loading loading={false} />}>
                          <ListStockCategories />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/products/cupons"
                      element={
                        <Suspense>
                          <ListCoupons />
                        </Suspense>
                      }
                    />
                  </>
                )}
              </>
            )}
            <Route
              path="/products/sales"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListAllProductsReadyForSale />
                </Suspense>
              }
            />

            {module_booking && (
              <Route
                path="/products/resources"
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListResource />
                  </Suspense>
                }
              />
            )}
          </>
        )}
        {/**Production Orders */}
        {allowRoles([
          'ADMIN',
          'CHIEF_PRODUCTION',
          'MANAGER_PRODUCTION',
          'MANAGER_COST_PRICES',
        ]) &&
          // business?.configurationsKey.find(
          //   (itm) => itm.key === 'module_production'
          // )?.value === 'true'
          businessPackages?.find((itm) => itm.code === 'M_PRODUCTION')
            ?.isActive && (
            <>
              <Route
                path="/orders"
                index={allowRoles(['CHIEF_PRODUCTION'], true)}
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListProductionOrders />
                  </Suspense>
                }
              />
              <Route
                path="orders/recipes"
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListRecipes />
                  </Suspense>
                }
              />
              <Route
                path="/orders/analysis"
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ProductionAnalysis />
                  </Suspense>
                }
              />
            </>
          )}
        {/**Economic Cycles */}
        {allowRoles([
          'ADMIN',
          'MANAGER_ECONOMIC_CYCLE',
          'MANAGER_SHIFT',
          'MANAGER_SALES',
          'ANALYSIS_REPORT',
          'MANAGER_BILLING',
        ]) && (
          <>
            {allowRoles([
              'ADMIN',
              'MANAGER_ECONOMIC_CYCLE',
              'MANAGER_SHIFT',
              'MANAGER_SALES',
            ]) && (
              <Route
                path="/ecocycle"
                index={allowRoles(
                  [
                    'ADMIN',
                    'MANAGER_ECONOMIC_CYCLE',
                    'MANAGER_SHIFT',
                    'MANAGER_SALES',
                  ],
                  true
                )}
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListEcoCyclesPage />
                  </Suspense>
                }
              />
            )}
            {allowRoles(['ADMIN', 'MANAGER_ECONOMIC_CYCLE']) && (
              <Route
                path="/ecocycle/accounts"
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <OrdersEcoCycle />
                  </Suspense>
                }
              />
            )}
            {allowRoles([
              'ADMIN',
              'ANALYSIS_REPORT',
              'MANAGER_SHOP_ONLINE',
              'MANAGER_SALES',
              'MANAGER_SHIFT',
              'MANAGER_BILLING',
            ]) && (
              <Route
                path="/ecocycle/analysis"
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <EconomicCyclesAnalysis />
                  </Suspense>
                }
              />
            )}
            <Route
              path="/ecocycle/:ecoCycleId"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailEcoCycleContainer />
                </Suspense>
              }
            />
          </>
        )}
        {/* Bank Account */}
        {allowRoles(['MANAGER_CONTABILITY', 'ANALYSIS_REPORT']) && (
          <>
            <Route
              index={allowRoles(['MANAGER_CONTABILITY'], true)}
              path="/bank_accounts"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListBankAccounts />
                </Suspense>
              }
            />
            <Route
              path="/bank_accounts/:bankAccountId"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <MainBankAccount />
                </Suspense>
              }
            />
            <Route
              path="/bank_accounts/reports"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <MainBankAccountReport />
                </Suspense>
              }
            />
            <Route
              path="/bank_accounts/lists"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListBalance />
                </Suspense>
              }
            />
            <Route
              path="/bank_accounts/analysis"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BankAccountAnalysis />
                </Suspense>
              }
            />
            <Route
              path="/bank_accounts/finance/state"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BankAccountFinancialState breadcrumb={true} />
                </Suspense>
              }
            />
          </>
        )}
        {/**Clients */}
        {allowRoles(['ADMIN', 'MANAGER_SHOP_ONLINE', 'MANAGER_CUSTOMERS']) && (
          <>
            <Route
              path="/clients/all"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <OnlineClientsContainer />
                </Suspense>
              }
            />
            <Route
              path="/clients/categories"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListCustomerCategory />
                </Suspense>
              }
            />
            <Route
              path="/clients/all/:clientId"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailOnlineClientContainer />
                </Suspense>
              }
            />
          </>
        )}
        {/**Human Resourses */}
        {allowRoles(['ADMIN', 'MANAGER_HUMAN_RESOURCES']) && (
          <>
            <Route
              path="/users"
              index={allowRoles(['MANAGER_HUMAN_RESOURCES'], true)}
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListUsers />
                </Suspense>
              }
            />
            <Route
              path="/human_resources/people"
              index={allowRoles(['MANAGER_HUMAN_RESOURCES'], true)}
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListPeople />
                </Suspense>
              }
            />
            <Route
              path="/human_resources/categories"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListCategories />
                </Suspense>
              }
            />
            <Route
              path="/human_resources/post"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListPost />
                </Suspense>
              }
            />
            <Route
              path="/human_resources/access"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AttendanceRecord />
                </Suspense>
              }
            />
          </>
        )}

        {/*Salarios*/}
        {allowRoles(['ADMIN', 'MANAGER_SALARY_RULES']) && (
          <>
            <Route
              path="/salary/generator"
              index={allowRoles(['MANAGER_SALARY_RULES'], true)}
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <Generator />
                </Suspense>
              }
            />

            <Route
              path="/salary/rules"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListRules />
                </Suspense>
              }
            />
            <Route
              path="/salary/historical"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <Historical />
                </Suspense>
              }
            />
            <Route
              path="/salary/historical/:historicalName/:historicalID"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <HistoricalDetails />
                </Suspense>
              }
            />
            <Route
              path="/salary/analysis"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <SalaryAnalysis />
                </Suspense>
              }
            />
          </>
        )}

        {/**Suppliers */}
        {business?.configurationsKey.find(
          (itm) => itm.key === 'visual_suppliers'
        )?.value === 'true' &&
          allowRoles(['ADMIN', 'MANAGER_SUPPLIERS']) && (
            <>
              <Route
                path="/suppliers"
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <ListSuppliers />
                  </Suspense>
                }
              />
              <Route
                path="/suppliers/:supplierId"
                element={
                  <Suspense fallback={<Loading loading={false} />}>
                    <DetailSupplier />
                  </Suspense>
                }
              />
            </>
          )}
        {/**Billing */}
        {
          // business?.configurationsKey.find((itm) => itm.key === 'module_billing')
          //   ?.value === 'true'
          businessPackages?.find((itm) => itm.code === 'M_BILLING')?.isActive &&
            allowRoles([
              'ADMIN',
              'MANAGER_SALES',
              'MANAGER_SHIFT',
              'MANAGER_BILLING',
              'MARKETING_SALES',
            ]) && (
              <>
                <Route
                  path="/billing/registers"
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <AllRegistersList />
                    </Suspense>
                  }
                />
                <Route
                  path="/billing/resume"
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <BillingSummary />
                    </Suspense>
                  }
                />
                <Route
                  path="/billing/overdue_payments"
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <OverduePayments />
                    </Suspense>
                  }
                />
                <Route
                  path="/billing/prepaid"
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <AllPrepaidList />
                    </Suspense>
                  }
                />
                {allowRoles([
                  'ADMIN',
                  'OWNER',
                  'MANAGER_BILLING',
                  'ANALYSIS_REPORT',
                ]) && (
                  <Route
                    path="/billing/analysis"
                    element={
                      <Suspense fallback={<Loading loading={false} />}>
                        <BillingAnalysis />
                      </Suspense>
                    }
                  />
                )}
              </>
            )
        }
        {allowRoles([
          'ADMIN',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
          'MANAGER_BILLING',
          'MARKETING_SALES',
          'MANAGER_SHIPPING',
        ]) && (
          <>
            <Route
              path="/registers-beta/all"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BillingRegisterList />
                </Suspense>
              }
            />
            <Route
              path="/registers-beta/all/:invoiceId"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <BillingRegister />
                </Suspense>
              }
            />
            <Route
              path="/cash-operations/all"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CashOperationsList />
                </Suspense>
              }
            />
          </>
        )}
        {/**Logistic */}
        {allowModules(['M_LOGISTICS']) && (
          <>
            <Route
              path="/logistic/containers"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ContainerList />
                </Suspense>
              }
            />
            <Route
              path="/logistic/files"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ExpedientList />
                </Suspense>
              }
            />
            <Route
              path="/logistic/shipment-notifications"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ShipmentNotificationList />
                </Suspense>
              }
            />
            <Route
              path="/logistic/companies"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CompanyList />
                </Suspense>
              }
            />
            <Route
              path="/logistic/categories"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CategoryList />
                </Suspense>
              }
            />
            <Route
              path="/logistic/vehicles"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <VehicleList />
                </Suspense>
              }
            />
            <Route
              path="/logistic/purchase-orders"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <PurchaseOrderList />
                </Suspense>
              }
            />
          </>
        )}

        {/**Reservations */}
        {allowRoles([
          'ADMIN',
          'OWNER',
          'MANAGER_BILLING',
          'ANALYSIS_REPORT',
        ]) && (
          <>
            <Route
              path="/reservation/calendar"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CalendarReservation />
                </Suspense>
              }
            />
            <Route
              path="/reservation/all"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AllReservations />
                </Suspense>
              }
            />
            <Route
              path="/reservation/adjustment"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ReservationSettingsMain />
                </Suspense>
              }
            />
          </>
        )}

        {/**Análisis */}
        {allowRoles(['ADMIN', 'ANALYSIS_REPORT', 'MANAGER_SHOP_ONLINE']) && (
          <>
            <Route
              path="/reports"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AllReports />
                </Suspense>
              }
            />
          </>
        )}

        {/**Store */}
        {allowRoles(['ADMIN', 'MANAGER_SHOP_ONLINE', 'MANAGER_SHIPPING']) && (
          <>
            <Route
              index={allowRoles(
                ['MANAGER_SHOP_ONLINE', 'MANAGER_SHIPPING'],
                true
              )}
              path="/online_shop"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListAllOrders />
                </Suspense>
              }
            />
          </>
        )}

        {/* Cartelera digital */}
        <Route
          index={allowRoles(['MANAGER_SHOP_ONLINE'], true)}
          path="/cart_digital"
          element={
            <Suspense fallback={<Loading loading={false} />}>
              <CartDigital />
            </Suspense>
          }
        />

        {/**Configurations */}
        {allowRoles([
          'ADMIN',
          'MANAGER_COST_PRICES',
          'MANAGER_CURRENCIES',
          'MANAGER_CONFIGURATIONS',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
        ]) && (
          <>
            <Route
              path="/configurations/my_business"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <MyBusiness />
                </Suspense>
              }
            />
            {/*<Route
              path="/configurations/my_areas"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ListAreas />
                </Suspense>
              }
            /> */}
            <Route
              path="/configurations/my_areas"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <AreaList />
                </Suspense>
              }
            />
            <Route
              path="/configurations/my_areas/:areaId"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <DetailAreaContainer />
                </Suspense>
              }
            />
            <Route
              path="/configurations/shared_areas"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <>
                    <Breadcrumb
                      icon={<Cog8ToothIcon className="h-6 text-gray-500" />}
                      paths={[
                        {
                          name: 'Configuraciones',
                        },
                        {
                          name: 'Áreas compartidas',
                        },
                      ]}
                    />
                    <ListShareAreas />
                  </>
                </Suspense>
              }
            />
            <Route
              path="/configurations/regions"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <RegionConfig />
                </Suspense>
              }
            />

            <Route
              path="/configurations/settings"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <ConfigMain />
                </Suspense>
              }
            />
            <Route
              path="configurations/pricessystem"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <PriceSystemConfig />
                </Suspense>
              }
            />
            <Route
              path="configurations/modules_services"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <>
                    <Breadcrumb
                      icon={<Cog8ToothIcon className="h-6 text-gray-500" />}
                      paths={[
                        {
                          name: 'Configuraciones',
                        },
                        {
                          name: 'Módulos y servicios',
                        },
                      ]}
                    />
                    <ListModulesServices />
                  </>
                </Suspense>
              }
            />
            <Route
              path="configurations/payment_gateways"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <>
                    <Breadcrumb
                      icon={<Cog8ToothIcon className="h-6 text-gray-500" />}
                      paths={[
                        {
                          name: 'Configuraciones',
                        },
                        {
                          name: 'Pasarelas de pago',
                        },
                      ]}
                    />
                    <PaymentGatewaysList />
                  </>
                </Suspense>
              }
            />
            <Route
              path="configurations/fixedcosts"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <FixedCosts />
                </Suspense>
              }
            />
            <Route
              path="/configurations/currencies"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <CurrenciesConfig />
                </Suspense>
              }
            />
            {
              // business?.configurationsKey.find(
              //   (itm) => itm.key === 'module_notifications'
              // )?.value === 'true'
              businessPackages?.find((itm) => itm.code === 'M_NOTIFICATIONS')
                ?.isActive && (
                <Route
                  path="/configurations/notifications"
                  element={
                    <Suspense fallback={<Loading loading={false} />}>
                      <NotificationConfig />
                    </Suspense>
                  }
                />
              )
            }
          </>
        )}

        {/**Cartelera Digital */}
        {allowRoles([
          'ADMIN',
          'MANAGER_COST_PRICES',
          'MANAGER_CURRENCIES',
          'MANAGER_CONFIGURATIONS',
          'MANAGER_SALES',
          'MANAGER_SHIFT',
        ]) && (
          <Route
            path="/digital_billboard"
            element={
              <Suspense fallback={<Loading loading={false} />}>
                <CartDigital />
              </Suspense>
            }
          />
        )}

        {/**Turns */}
        {allowRoles(['ADMIN', 'MANAGER_ACCESS_POINT']) && (
          <>
            <Route
              path="/turns"
              element={
                <Suspense fallback={<Loading loading={false} />}>
                  <TurnPage />
                </Suspense>
              }
            />
          </>
        )}

        {/**Notification */}
        <Route
          path="/notifications"
          element={
            <Suspense fallback={<Loading loading={false} />}>
              <NotificationConfig />
            </Suspense>
          }
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default PayedRoute
